/**
 * Service for handling chemistry table import and R-group decomposition
 */

import { clerkService } from './clerkService';

const API_BASE_URL = import.meta.env?.VITE_API_URL || 'http://localhost:5001';

/**
 * Upload and process a chemistry table file with optional generic molecule
 * @param {File} file - The CSV or Excel file to upload
 * @param {string} genericMolecule - Optional SMILES string for scaffold
 * @returns {Promise<Object>} R-group decomposition results
 */
export async function uploadChemistryTable(file, genericMolecule = '') {
    const formData = new FormData();
    formData.append('file', file);

    if (genericMolecule && genericMolecule.trim()) {
        formData.append('genericMolecule', genericMolecule.trim());
    }

    try {
        // Get auth token
        const token = await clerkService.getToken();
        const headers = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        // Let browser set Content-Type with boundary for multipart/form-data

        const response = await fetch(`${API_BASE_URL}/api/chemistry/upload`, {
            method: 'POST',
            body: formData,
            credentials: 'include',
            headers: headers
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to upload chemistry table');
        }

        const results = await response.json();

        // Add filename to results for display
        results.filename = file.name;
        results.uploadedAt = new Date().toISOString();

        return results;
    } catch (error) {
        console.error('Error uploading chemistry table:', error);
        throw error;
    }
}

/**
 * Check if chemistry service (RDKit) is available
 * @returns {Promise<Object>} Health status
 */
export async function checkChemistryHealth() {
    try {
        const response = await fetch(`${API_BASE_URL}/api/chemistry/health`, {
            method: 'GET',
            credentials: 'include'
        });

        if (!response.ok) {
            throw new Error('Chemistry service health check failed');
        }

        return await response.json();
    } catch (error) {
        console.error('Error checking chemistry health:', error);
        return {
            status: 'unhealthy',
            rdkit_available: false,
            error: error.message
        };
    }
}

/**
 * Format R-group decomposition results for display
 * @param {Array} rgroupData - R-group data from API
 * @returns {Array} Formatted data
 */
export function formatRGroupData(rgroupData) {
    if (!rgroupData || rgroupData.length === 0) {
        return [];
    }

    return rgroupData.map(row => {
        // Create a formatted version of the row
        const formatted = { ...row };

        // Format SMILES if present
        if (row.SMILES) {
            formatted.SMILES = row.SMILES;
        }

        return formatted;
    });
}

/**
 * Generate HTML table from R-group data
 * @param {Array} rgroupData - R-group decomposition data
 * @param {Array} columns - Column names
 * @returns {string} HTML table string
 */
export function generateRGroupTableHTML(rgroupData, columns) {
    if (!rgroupData || rgroupData.length === 0) {
        return '<p>No data available</p>';
    }

    let html = '<table class="rgroup-table">';
    html += '<thead><tr>';

    // Add column headers
    columns.forEach(col => {
        html += `<th>${col}</th>`;
    });

    html += '</tr></thead>';
    html += '<tbody>';

    // Add data rows
    rgroupData.forEach(row => {
        html += '<tr>';
        columns.forEach(col => {
            const value = row[col] !== undefined && row[col] !== null ? row[col] : '-';
            html += `<td>${value}</td>`;
        });
        html += '</tr>';
    });

    html += '</tbody></table>';
    return html;
}

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} Success status
 */
export async function copyToClipboard(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (error) {
        console.error('Failed to copy to clipboard:', error);
        // Fallback method
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            document.body.removeChild(textArea);
            return true;
        } catch (err) {
            document.body.removeChild(textArea);
            return false;
        }
    }
}
